<script setup lang="ts">
import type { PlanName } from '~/composables/usePlanConfig'
import type { Competitor } from '~~/shared/compare/types'
import { COMPETITORS, alternativePath, getCompetitor } from '~~/shared/compare'
import { MYCHAR_PLANS, formatCheckedMonth } from '~~/shared/compare/rows'

// One comparison page per competitor at /{slug}-alternative. Routes are
// registered from shared/compare in nuxt.config.ts (pages:extend) as static
// paths, so they never shadow other usernames, and are prerendered.
// Deliberately not linked from the header or footer: reached via the
// sitemap, llms.txt, the /alternatives hub, blog posts, and each other.

const route = useRoute()
// The slug comes from route meta (set in pages:extend), with the path as fallback
const found = getCompetitor(String(route.meta.competitor ?? route.path.replace(/^\/|-alternative\/?$/g, '')))

if (!found) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

const competitor: Competitor = found

const siteUrl = useRuntimeConfig().public.siteUrl
const path = alternativePath(competitor.slug)
const checkedMonth = formatCheckedMonth(competitor.checkedOn)

useSeoMeta({
  title: competitor.seo.title,
  // seo.title already carries the brand; skip the global "%s - mychar.gg" suffix
  titleTemplate: (t?: string | null) => (t && /mychar\.gg/i.test(t) ? t : `${t} | mychar.gg`),
  ogTitle: competitor.seo.title,
  description: competitor.seo.description,
  ogDescription: competitor.seo.description
})

defineOgImageComponent('Saas', {
  title: `mychar.gg vs ${competitor.name}`,
  description: competitor.hero.title
})

const breadcrumb = [
  { label: 'Home', to: '/' },
  { label: 'Alternatives', to: '/alternatives' },
  { label: `${competitor.name} alternative`, to: path }
]

// FAQ copy may contain [label](url) links; schema answers get plain text
const stripLinks = (text: string) => text.replace(/\[([^\]]+)\]\([^)\s]+\)/g, '$1')

useSchemaOrg([
  defineWebPage({
    '@type': ['WebPage', 'FAQPage'],
    'dateModified': competitor.checkedOn
  }),
  defineBreadcrumb({
    itemListElement: breadcrumb.map(item => ({ name: item.label, item: `${siteUrl}${item.to === '/' ? '' : item.to}` }))
  }),
  ...competitor.faq.map(item => defineQuestion({ name: item.label, acceptedAnswer: stripLinks(item.content) }))
])

// The editor is the headline differentiator on every comparison
const editorFeatures = [
  {
    title: 'Edit on the page itself',
    description: 'Click your name or bio to change it. No settings form between you and the result.',
    icon: 'i-lucide-mouse-pointer-click'
  },
  {
    title: 'Drag and drop',
    description: 'Drag sections, links, and widgets like the music player into the order and spot you want.',
    icon: 'i-lucide-move'
  },
  {
    title: 'Every change shows live',
    description: 'Fonts, colors, layouts, and effects update the moment you pick them, exactly as visitors see them.',
    icon: 'i-lucide-eye'
  }
]

// Plan cards: signed-out visitors go to signup, signed-in users to /pricing
// (checkout lives there)
const authStore = useAuthStore()
const paymentMode = (useRuntimeConfig().public.paymentMode as 'onetime' | 'subscription') || 'onetime'
const plans = planCardsForMode(paymentMode === 'onetime')

function getButtonLabel(plan: PlanName): string {
  if (authStore.userId) return plan === authStore.plan ? 'Current Plan' : 'View plans'
  if (plan === 'free') return 'Get Started'
  return plan === 'pro' ? 'Get Pro' : 'Get Elite'
}

async function handlePlanAction() {
  await navigateTo(authStore.userId ? '/pricing' : '/signup')
}

const otherComparisons = COMPETITORS.filter(c => c.slug !== competitor.slug)
</script>

<template>
  <div>
    <UPageHero
      :title="competitor.hero.title"
      :description="competitor.hero.description"
      orientation="horizontal"
      :ui="{
        container: 'lg:gap-16',
        title: 'max-w-xl lg:text-5xl',
        description: 'max-w-lg',
        links: 'w-full'
      }"
    >
      <template #headline>
        <UBreadcrumb :items="breadcrumb" />
      </template>

      <template #links>
        <div class="flex w-full flex-col gap-5">
          <HeroClaimUsername />
          <ul class="flex flex-wrap items-center gap-x-5 gap-y-2">
            <li
              v-for="item in ['Free forever', 'No credit card needed', 'Pay once if you upgrade']"
              :key="item"
              class="flex items-center gap-1.5 text-sm text-muted"
            >
              <UIcon
                name="i-lucide-check"
                class="size-4 text-primary-400"
              />
              {{ item }}
            </li>
          </ul>
        </div>
      </template>

      <HeroPreview />
    </UPageHero>

    <!-- The short answer: written to stand on its own when quoted -->
    <UContainer class="pb-4">
      <div class="grid gap-4 lg:grid-cols-2">
        <div class="rounded-2xl border border-default bg-elevated/40 p-6 shadow-(--surface-shadow) sm:p-8 lg:col-span-2">
          <h2 class="text-xl font-semibold text-highlighted sm:text-2xl">
            mychar.gg vs {{ competitor.name }}: the short answer
          </h2>
          <p class="mt-3 text-base text-muted sm:text-lg">
            <CompareText :text="competitor.summary" />
          </p>
        </div>

        <div class="rounded-2xl border border-primary-400/40 bg-primary-500/4 p-6 shadow-(--surface-shadow)">
          <h3 class="font-semibold text-highlighted">
            Choose mychar.gg if you
          </h3>
          <ul class="mt-3 flex flex-col gap-2.5">
            <li
              v-for="item in competitor.chooseMychar"
              :key="item"
              class="flex items-start gap-2.5 text-sm text-muted"
            >
              <UIcon
                name="i-lucide-check"
                class="mt-0.5 size-4 shrink-0 text-primary-400"
              />
              {{ item }}
            </li>
          </ul>
        </div>

        <div class="rounded-2xl border border-default bg-elevated/40 p-6 shadow-(--surface-shadow)">
          <h3 class="font-semibold text-highlighted">
            Stay with {{ competitor.name }} if you
          </h3>
          <ul class="mt-3 flex flex-col gap-2.5">
            <li
              v-for="item in competitor.chooseThem"
              :key="item"
              class="flex items-start gap-2.5 text-sm text-muted"
            >
              <UIcon
                name="i-lucide-arrow-right"
                class="mt-0.5 size-4 shrink-0 text-dimmed"
              />
              {{ item }}
            </li>
          </ul>
        </div>
      </div>
    </UContainer>

    <!-- Feature and pricing table -->
    <UPageSection
      id="compare"
      :title="`mychar.gg vs ${competitor.name}, feature by feature`"
      :description="`Design, content, integrations, analytics, and pricing side by side. Checked ${checkedMonth}.`"
    >
      <CompareTable
        :competitor="competitor"
        class="mx-auto w-full max-w-5xl"
      />

      <div class="mx-auto mt-12 grid w-full max-w-5xl gap-4 md:grid-cols-2">
        <ComparePlanList
          title="mychar.gg"
          :plans="MYCHAR_PLANS"
          note="Prices in EUR, taxes included. Pro and Elite are paid once and kept forever."
          highlight
        />
        <ComparePlanList
          :title="competitor.name"
          :plans="competitor.plans"
          :note="competitor.plansNote"
        />
      </div>

      <p class="mx-auto mt-6 flex max-w-5xl items-start justify-center gap-1.5 text-center text-sm text-muted">
        <UIcon
          name="i-lucide-calendar-check"
          class="mt-0.5 size-4 shrink-0"
        />
        <span>
          Pricing and features checked {{ checkedMonth }}. See
          <ULink
            raw
            :to="competitor.pricingUrl"
            target="_blank"
            rel="nofollow noopener"
            class="font-medium text-primary underline decoration-primary/30 underline-offset-4 hover:decoration-primary"
          >{{ competitor.name }}'s site</ULink>
          for their latest pricing.
        </span>
      </p>
    </UPageSection>

    <!-- What we have that they do not -->
    <UPageSection
      :title="`What mychar.gg has that ${competitor.name} does not`"
      description="Things you can only do on mychar.gg, or that cost extra elsewhere."
      :features="competitor.advantages"
    />

    <!-- The editor -->
    <UPageSection
      headline="The editor"
      title="Build it on the page and see it live"
      description="The editor is your actual page. Edit text in place, drag things where you want them, and style everything from a sidebar while the page updates as you go. Try a theme and a font."
      orientation="horizontal"
      :features="editorFeatures"
    >
      <ShowcaseDesign />
    </UPageSection>

    <!-- Honest strengths of the competitor -->
    <UPageSection
      :title="`Where ${competitor.name} is stronger`"
      :description="`We make mychar.gg, so here is the other side. ${competitor.name} does these things better.`"
    >
      <div class="mx-auto grid w-full max-w-5xl gap-4 sm:grid-cols-2">
        <div
          v-for="item in competitor.strengths"
          :key="item.title"
          class="rounded-2xl border border-default bg-elevated/40 p-6 shadow-(--surface-shadow)"
        >
          <h3 class="font-semibold text-highlighted">
            {{ item.title }}
          </h3>
          <p class="mt-2 text-sm text-muted">
            {{ item.description }}
          </p>
        </div>
      </div>
    </UPageSection>

    <!-- Long-form: why people switch, how the products differ, how to switch -->
    <UContainer class="py-8 sm:py-12">
      <div class="mx-auto flex max-w-3xl flex-col gap-12">
        <section
          v-for="section in competitor.sections"
          :key="section.title"
        >
          <h2 class="text-2xl font-bold text-highlighted sm:text-3xl">
            {{ section.title }}
          </h2>
          <p
            v-for="(paragraph, i) in section.paragraphs"
            :key="i"
            class="mt-4 text-base leading-relaxed text-muted sm:text-lg"
          >
            <CompareText :text="paragraph" />
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-highlighted sm:text-3xl">
            How to switch from {{ competitor.name }} to mychar.gg
          </h2>
          <ol class="mt-6 flex flex-col gap-5">
            <li
              v-for="(step, i) in competitor.switchSteps"
              :key="step.title"
              class="flex gap-4"
            >
              <span class="flex size-8 shrink-0 items-center justify-center rounded-full border border-primary-400/30 bg-primary-500/10 text-sm font-semibold tint-primary">
                {{ i + 1 }}
              </span>
              <div>
                <h3 class="font-semibold text-highlighted">
                  {{ step.title }}
                </h3>
                <p class="mt-1 text-base text-muted">
                  <CompareText :text="step.description" />
                </p>
              </div>
            </li>
          </ol>
        </section>
      </div>
    </UContainer>

    <!-- Our plans -->
    <UPageSection
      id="plans"
      title="mychar.gg plans"
      description="Start free and upgrade only if you want more. Pro and Elite are one-time payments."
    >
      <PlanCards
        :plans="plans"
        billing-cycle="monthly"
        :checkout-loading="null"
        :get-button-label="getButtonLabel"
        :payment-mode="paymentMode"
        class="mx-auto w-full max-w-5xl"
        @plan-action="handlePlanAction"
      />
      <p class="mt-6 text-center text-sm text-muted">
        Want your own web address? The custom domain add-on is €2.99 per month on any plan.
        <ULink
          raw
          to="/pricing#compare"
          class="font-medium text-primary underline decoration-primary/30 underline-offset-4 hover:decoration-primary"
        >
          Compare every plan feature
        </ULink>
      </p>
    </UPageSection>

    <!-- FAQ -->
    <UPageSection
      title="Frequently asked questions"
      :description="`Switching from ${competitor.name}, pricing, and what carries over.`"
    >
      <UAccordion
        :items="competitor.faq"
        :unmount-on-hide="false"
        type="multiple"
        class="mx-auto flex w-full max-w-3xl flex-col gap-3"
        :ui="{
          item: 'rounded-xl border border-default bg-elevated/40 px-4 border-b-0 last:border-b-0 shadow-(--surface-shadow)',
          trigger: 'text-base text-highlighted',
          body: 'text-base text-muted'
        }"
      >
        <template #body="{ item }">
          <CompareText :text="item.content" />
        </template>
      </UAccordion>
    </UPageSection>

    <!-- Other comparisons (internal links between the comparison pages) -->
    <UContainer class="pb-4">
      <div class="mx-auto flex max-w-5xl flex-col items-center gap-4 text-center">
        <h2 class="text-lg font-semibold text-highlighted">
          More comparisons
        </h2>
        <div class="flex flex-wrap justify-center gap-2">
          <UButton
            v-for="other in otherComparisons"
            :key="other.slug"
            :to="alternativePath(other.slug)"
            :label="`${other.name} alternative`"
            color="neutral"
            variant="outline"
            size="sm"
          />
          <UButton
            to="/alternatives"
            label="All comparisons"
            color="neutral"
            variant="ghost"
            size="sm"
            trailing-icon="i-lucide-arrow-right"
          />
        </div>
      </div>
    </UContainer>

    <CtaPanel
      :title="`Try the ${competitor.name} alternative you design yourself`"
      description="Claim your username and build your page in minutes. Free forever."
    />
  </div>
</template>
